import type {
  AmhsFoup,
  AmhsFoupPriority,
  AmhsInventoryConfig,
  AmhsRunRequest,
} from '../../types/amhsSimulationRequest';

export const DEFAULT_FOUP_COUNT = 1;
/** Drop-to-pick delay in wall seconds (live payload sends value × simSpeed). */
export const DEFAULT_DROP_TO_PICK_SEC = 1;

/**
 * Fixed `/amhs/run` inventory block (sample AMHS contract).
 * Not derived from Simulation panel metadata.
 */
export const AMHS_RUN_INVENTORY: AmhsInventoryConfig = {
  enabled: true,
  foupCapacity: 2,
  stockerColumns: [
    {
      kind: 'raw',
      name: 'Raw FOUP',
      inventoryLevel: 'stocker',
      depositFleet: 'interbay',
      withdrawFleet: 'intrabay',
      initialFoups: 0,
    },
    {
      kind: 'processed',
      name: 'Processed FOUP',
      inventoryLevel: 'bay',
      depositFleet: 'intrabay',
      withdrawFleet: 'interbay',
      initialFoups: 0,
    },
  ],
};

const FOUP_PRIORITY_CYCLE: AmhsFoupPriority[] = ['CRITICAL', 'HIGH', 'NORMAL', 'LOW'];

export function buildDefaultFoups(count = DEFAULT_FOUP_COUNT): { foups: AmhsFoup[] } {
  return {
    foups: Array.from({ length: count }, (_, index) => {
      const suffix = String(index + 1).padStart(2, '0');
      return {
        foupId: `FOUP-${suffix}`,
        lotId: `LOT-${suffix}`,
        status: 'Available' as const,
        // Default stagger for manual mode; auto|gap|serial overrides in buildAmhsRunRequest.
        arrivalTimeSec: index * 5,
        priority: FOUP_PRIORITY_CYCLE[index % FOUP_PRIORITY_CYCLE.length],
      };
    }),
  };
}

type SimulationConfig = Pick<
  AmhsRunRequest,
  | 'persist'
  | 'includeSimulation'
  | 'includeKinetic'
  | 'includeTransportFlow'
  | 'includeTransportRequests'
  | 'includeTransportationGraph'
  | 'includeReports'
  | 'includeTrOptimization'
  | 'includeRouteOptimization'
  | 'routeOptimization'
  | 'includeCollisionAvoidance'
  | 'collisionAvoidance'
  | 'includeOhtAssignment'
  | 'ohtAssignment'
  | 'inventory'
  | 'includeUtilizationOptimization'
  | 'utilizationOptimization'
  | 'includeTransportPrioritization'
  | 'transportPrioritization'
>;

export const AMHS_PHYSICS_DEFAULTS = {
  trackSpeed: 2.5,
  trackCapacity: 1,
  nodeCapacity: 1,
  minHeadwaySec: 2,
  /** Travel speed (m/s). Travel time ≈ path length ÷ cruiseSpeed. */
  ohtCruiseSpeed: 2.5,
  /**
   * Vehicle body length (m). Documented for CA / twin data; not driving
   * schedule logic by itself today.
   */
  ohtLengthM: 1.2,
  /**
   * Minimum gap behind another OHT (m). Keep at 2.
   * CA headway ≈ minSeparationM / cruiseSpeed seconds (plus global headway).
   */
  ohtMinSeparationM: 2,
} as const;

export const AMHS_SIMULATION_CONFIG: SimulationConfig = {
  persist: false,
  includeSimulation: true,
  includeKinetic: false,
  includeTransportFlow: true,
  includeTransportRequests: true,
  includeTransportationGraph: true,
  includeReports: true,
  includeTrOptimization: true,
  includeRouteOptimization: true,
  routeOptimization: {
    enabled: true,
    maxCandidates: 3,
    objective: 'minWait',
    compareBaseline: true,
  },
  // CA on — FOUP hop chaining prevents Exit stealing pre-process RAW.
  includeCollisionAvoidance: true,
  collisionAvoidance: {
    enabled: true,
    mode: 'jointPriority',
    jointForce: true,
    jointReleaseSlackSec: 120,
    headwaySec: 25,
    trackOccupancy: 'exclusiveBlock',
    allowOppositeTraffic: false,
    onConflict: 'wait',
    fallback: 'reroute',
    // Shared docks by default; exclusive docks starve the fleet (the vehicle on
    // the dock takes every job from it). Junction capacity is a separate rule
    // the live API applies by default.
    enforceNodeCapacity: false,
    defaultNodeCapacity: 1,
    enforceMinSeparation: true,
    // Prefer util-friendly CA: no forced home return (extends makespan without
    // productive work); absorb wait into prior segment for denser schedules.
    absorbWaitIntoPreviousSegment: true,
    nodeClearanceSec: 5,
    /** Hold at stocker/tool nodes through the 9s hoist so PICKUP stamps on-dock. */
    minNodeHoldSec: 12,
    returnHomeAfterLastJob: false,
  },
  includeOhtAssignment: true,
  ohtAssignment: {
    enabled: true,
    policies: ['fifo', 'greedy', 'ortools'],
    compareBaseline: true,
    preferPreferredOht: true,
    allowIntrabayEmptyTravel: true,
    intelligentSharedOht: false,
    sharedOhtMinGainSec: 30,
    useOrtools: true,
    intelligentDedicatedAssist: false,
    dedicatedJobsFirst: true,
  },
  /** Master switch for inventory wait/gating on `/amhs/run`. */
  inventory: AMHS_RUN_INVENTORY,
  includeUtilizationOptimization: true,
  utilizationOptimization: {
    enabled: true,
    objective: 'maxUtilization',
    fleetCandidates: [],
    minUtilization: 0,
    maxUtilization: 1,
    primaryTradeoff: 'makespan',
    evaluateReturnHomeVariants: true,
  },
  includeTransportPrioritization: true,
  transportPrioritization: {
    enabled: true,
    levels: ['CRITICAL', 'HIGH', 'NORMAL', 'LOW'],
    defaultPriority: 'NORMAL',
    applyToDispatch: true,
    applyToRouteOptimization: true,
    applyToCollisionAvoidance: true,
  },
};
